import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { ExportsExportCreatedResponse } from '@customerio/hydra-ui-api';
import { hydraUiApi } from '@/services/api';
import type { HookMutationConfig } from '../shared/react-query-types';
import { requireWorkspaceId } from '../shared/workspace';
import { environmentPath, exportPath } from './api-path';
import { exportKeys } from './entity';

/* ------------------------------------------------------------------------- */
/* cancelExport                                                               */
/* ------------------------------------------------------------------------- */

export interface CancelExportInput {
  workspaceId: number;
  id: string;
}

/**
 * Cancel a queued or in-progress export. The backend cancels the backing
 * backfill and marks the export failed; responds 204 (or 422 if the export
 * already reached a terminal state).
 */
export async function cancelExport(input: CancelExportInput): Promise<void> {
  await hydraUiApi.exports.cancel({ params: { path: exportPath(input.workspaceId, input.id) } });
}

interface UseCancelExportOptions {
  workspaceId: number;
  mutationConfig?: HookMutationConfig<void, CancelExportInput>;
}

/** @public */
export const useCancelExport = ({ workspaceId, mutationConfig }: UseCancelExportOptions) => {
  const qc = useQueryClient();
  const { onSettled, ...rest } = mutationConfig ?? {};
  return useMutation({
    ...rest,
    mutationFn: (variables: CancelExportInput) => cancelExport(variables),
    onSettled: (...callbackArgs) => {
      // Cancel responds 204 with no body, so refetch the list to pick up the
      // export's new failed state. Also refetch on error: a 422 means the export
      // reached a terminal state on the server while the row still showed
      // "Processing", so the list is stale either way.
      void qc.invalidateQueries({ queryKey: exportKeys.lists(workspaceId) });
      onSettled?.(...callbackArgs);
    },
  });
};

/* ------------------------------------------------------------------------- */
/* exportOther                                                                */
/* ------------------------------------------------------------------------- */

/** The people page's "Export other" kinds (Ember's `OtherExportType`). */
export type OtherExportType = 'devices' | 'duplicated_attribute';

export interface ExportOtherInput {
  workspaceId: number;
  exportType: OtherExportType;
  /** Attribute columns beyond the always-included set. */
  attributes: string[];
  /** The attribute to find duplicates of; null for devices exports. */
  duplicatedAttribute: string | null;
}

/**
 * Queue a devices or duplicated-attribute CSV export. Completes out of band —
 * the user is emailed when the file is ready and the row shows up on the
 * exports page.
 */
export async function exportOther(input: ExportOtherInput): Promise<ExportsExportCreatedResponse> {
  const path = environmentPath(input.workspaceId);
  if (input.exportType === 'duplicated_attribute') {
    if (input.duplicatedAttribute == null) {
      throw new Error('A duplicated-attribute export requires the attribute to deduplicate');
    }
    return hydraUiApi.exports.duplicatedAttribute({
      params: { path },
      body: { attribute: input.duplicatedAttribute, attributes: input.attributes },
    });
  }
  return hydraUiApi.exports.devices({ params: { path }, body: { attributes: input.attributes } });
}

/* ------------------------------------------------------------------------- */
/* exportEmailSuppressions                                                    */
/* ------------------------------------------------------------------------- */

export interface ExportEmailSuppressionsInput {
  workspaceId: number;
}

/**
 * Queue a CSV of the workspace's suppressed addresses. Takes no body and
 * completes out of band — the user is emailed when the file is ready and the
 * row shows up on the exports page.
 */
export async function exportEmailSuppressions(
  input: ExportEmailSuppressionsInput,
): Promise<ExportsExportCreatedResponse> {
  return hydraUiApi.exports.emailSuppressions({ params: { path: environmentPath(input.workspaceId) } });
}

interface UseExportEmailSuppressionsOptions {
  workspaceId?: number; // override; defaults from the URL
  mutationConfig?: HookMutationConfig<ExportsExportCreatedResponse, void>;
}

/** @public */
export const useExportEmailSuppressions = ({
  workspaceId: overrideWorkspaceId,
  mutationConfig,
}: UseExportEmailSuppressionsOptions = {}) => {
  const workspaceId = overrideWorkspaceId ?? requireWorkspaceId();
  const qc = useQueryClient();
  const { onSuccess, ...rest } = mutationConfig ?? {};
  return useMutation({
    ...rest,
    mutationFn: () => exportEmailSuppressions({ workspaceId }),
    onSuccess: (created, ...callbackArgs) => {
      void qc.invalidateQueries({ queryKey: exportKeys.lists(workspaceId) });
      onSuccess?.(created, ...callbackArgs);
    },
  });
};
